import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

const SECTORS = [
  {
    id: "commercial-property",
    num: "01",
    title: "Commercial Property",
    subtitle: "Well-located, income-producing assets built for long-term returns",
    image: "/commercial-property.jpg",
    highlights: ["Income-Producing Assets", "Strong Tenants", "Capital Growth"],
    overview:
      "Our commercial property portfolio includes well-located, income-producing assets with the potential to deliver reliable long-term returns and capital growth. We focus on properties supported by strong tenants, sound underlying demand and future development potential.",
    stats: [
      ["Focus", "Well-located, income-producing assets"],
      ["Approach", "Reliable long-term returns & capital growth"],
      ["Criteria", "Strong tenants & development potential"],
    ],
  },
  {
    id: "land-development",
    num: "02",
    title: "Land Development",
    subtitle: "From acquisition and planning through to completion",
    image: "/land-subdivision.jpg",
    highlights: ["Acquisition To Completion", "Thoughtful Design", "Local Demand"],
    overview:
      "Jamsac Investments participates in land-development projects from acquisition and planning through to construction and completion. We seek projects where thoughtful design, disciplined delivery and a clear understanding of local demand can create lasting value.",
    stats: [
      ["Scope", "Acquisition, planning, construction, completion"],
      ["Approach", "Thoughtful design & disciplined delivery"],
      ["Focus", "Lasting value from local demand"],
    ],
  },
  {
    id: "financial-services",
    num: "03",
    title: "Financial Services",
    subtitle: "Equity and debt financing for select businesses",
    image: "/financial-services-v3.jpg",
    highlights: ["Equity & Debt Financing", "Sustainable NPV & IRR", "Growth Planning"],
    overview:
      "Jamsac provides equity and debt financing for select businesses, while seeking a long-term, positive and sustainable NPV and IRR on all projects — allowing businesses to make informed decisions and plan confidently for future growth.",
    stats: [
      ["Offering", "Equity & debt financing"],
      ["Benchmarks", "Positive, sustainable NPV & IRR"],
      ["Horizon", "Long term"],
    ],
  },
  {
    id: "storage-logistics",
    num: "04",
    title: "Storage & Logistics",
    subtitle: "Secure storage and dependable distribution networks",
    image: "/storage-logistics.jpg",
    highlights: ["Secure Storage", "Efficient Supply Chains", "Flexible Solutions"],
    overview:
      "Our storage and logistics investments support the movement, management and secure storage of goods. We look for businesses and facilities positioned to benefit from growing demand for efficient supply chains, flexible storage solutions and dependable distribution networks.",
    stats: [
      ["Focus", "Movement, management & secure storage"],
      ["Demand Drivers", "Efficient, flexible supply chains"],
      ["Networks", "Dependable distribution"],
    ],
  },
  {
    id: "import-distribution",
    num: "05",
    title: "Import & Distribution",
    subtitle: "Connecting quality products with growing markets",
    image: "/import-distribution.jpg",
    highlights: ["Supplier Relationships", "Efficient Operations", "Scalable Reach"],
    overview:
      "Jamsac invests in import and distribution businesses that connect quality products with established and emerging markets. We value strong supplier relationships, efficient operations and businesses with the ability to scale their reach over time.",
    stats: [
      ["Focus", "Established & emerging markets"],
      ["Values", "Supplier relationships, efficient operations"],
      ["Potential", "Ability to scale over time"],
    ],
  },
  {
    id: "construction",
    num: "06",
    title: "Construction",
    subtitle: "Quality commercial and residential construction",
    image: "/construction-v5.jpg",
    imgPosition: "object-bottom",
    highlights: ["Commercial & Residential", "Experienced Teams", "Reliable Delivery"],
    overview:
      "Our construction business delivers quality commercial and residential construction. We value experienced teams, sound project management and reliable delivery, with a focus on meeting local demand and building lasting value.",
    stats: [
      ["Sectors", "Commercial & residential"],
      ["Values", "Experienced teams, sound project management"],
      ["Focus", "Local demand & lasting value"],
    ],
  },
];

const fade = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: "easeOut" } },
};

export default function Sectors() {
  const [active, setActive] = useState(null);

  return (
    <section id="sectors" data-testid="sectors-section" className="py-20 lg:py-28 bg-white border-t border-[#E8F0F6]">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <motion.div
          variants={fade}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-80px" }}
          className="max-w-2xl mb-14"
        >
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#12344E] leading-tight">
            Portfolio Sectors
          </h2>
          <p className="mt-5 text-base text-[#4A6076] leading-relaxed">
            Each holding is chosen to stand on its own — and to strengthen the
            businesses beside it. Select a sector to learn more.
          </p>
        </motion.div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {SECTORS.map((s, i) => (
            <motion.button
              key={s.id}
              data-testid={`sector-card-${s.id}`}
              onClick={() => setActive(s)}
              variants={fade}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-60px" }}
              transition={{ delay: (i % 3) * 0.1 }}
              className="group bg-white rounded-lg overflow-hidden border border-[#E8F0F6] shadow-[0_6px_24px_rgba(18,52,78,0.08)] hover:shadow-[0_16px_44px_rgba(18,52,78,0.16)] hover:-translate-y-1 transition-all duration-300 text-left"
            >
              <div className="relative h-48 overflow-hidden">
                <img
                  src={s.image}
                  alt={s.title}
                  className={`absolute inset-0 block w-full h-full object-cover transition-transform duration-700 group-hover:scale-105 ${s.imgPosition || ""}`}
                />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-[#12344E] group-hover:text-[#1F6191] transition-colors duration-300">
                  {s.title}
                </h3>
                <p className="text-sm text-[#4A6076] leading-relaxed mt-2 min-h-[40px]">{s.subtitle}</p>
                <span className="inline-flex items-center gap-1.5 mt-4 text-xs font-bold uppercase tracking-[0.12em] text-[#1F6191]">
                  Learn More
                  <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
                </span>
              </div>
            </motion.button>
          ))}
        </div>
      </div>

      <Dialog open={!!active} onOpenChange={() => setActive(null)}>
        <DialogContent
          data-testid="sector-detail-dialog"
          className="bg-white border border-[#D5E2EC] text-[#12344E] max-w-2xl p-0 overflow-hidden rounded-xl"
        >
          {active && (
            <div>
              <div className="relative h-56">
                <img src={active.image} alt={active.title} className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-white via-transparent to-transparent" />
                <span className="absolute top-4 left-6 font-mono text-xs tracking-[0.3em] text-white bg-[#12344E]/60 backdrop-blur-sm px-3 py-1.5 rounded-full">
                  {active.num} / 06
                </span>
              </div>
              <div className="p-8 pt-2">
                <DialogHeader>
                  <DialogTitle className="text-3xl font-bold text-[#12344E]">
                    {active.title}
                  </DialogTitle>
                  <DialogDescription className="text-[#4A6076] text-sm">
                    {active.subtitle}
                  </DialogDescription>
                </DialogHeader>
                <p className="mt-5 text-sm text-[#4A6076] leading-relaxed">{active.overview}</p>
                <div className="flex flex-wrap gap-2 mt-5">
                  {active.highlights.map((h) => (
                    <span
                      key={h}
                      className="text-[10px] font-bold uppercase tracking-[0.15em] px-3 py-1.5 rounded-full bg-[#E8F0F6] text-[#1F6191]"
                    >
                      {h}
                    </span>
                  ))}
                </div>
                <div className="mt-6 border-t border-[#D5E2EC] divide-y divide-[#E8F0F6]">
                  {active.stats.map(([k, v]) => (
                    <div key={k} className="flex justify-between items-center py-3.5">
                      <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#1F6191]">{k}</span>
                      <span className="text-sm font-medium text-[#12344E]">{v}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
